import * as React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import axiosInstant from '../../api';
import { myInfo } from '../../api/endpoints';
import { ISinglePro } from '../../components/singlePro/state';
import { uploadVideo } from '../pro/recording/helper';
import { DotLottleLoader } from '../../components/Loader';

interface ICommonProps {
  keyName: string;
  user?: ISinglePro;
}

interface IDirectUpload extends ICommonProps {
  onComplete: (url: string) => void;
  variant?: 'text' | 'outlined' | 'contained'
}
export const DirectUpload: React.FC<IDirectUpload> = ({ keyName, onComplete, variant = 'outlined', ...rest }) => {
  const uploadRef = React.useRef<HTMLInputElement>(null);
  const [error, setError] = React.useState('');
  const [uploading, setUploading] = React.useState(false);

  const uploadDirectly = () => uploadRef.current?.click();
  const onUploadVideo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const target = files[0];
      if (target.size < 200000000) {
        setUploading(true);
        const fReader = new FileReader();
        fReader.readAsDataURL(target);
        fReader.onloadend = async () => {
          const videoUrl = await uploadVideo(target, keyName);
          if (videoUrl) {
            onComplete(videoUrl);
            setUploading(false);
          } else setError('Something went wrong');
        };
      } else setError('File exceeds maximum upload size of 200MB.');
    }
  };

  return (
    <>
      <Button variant={variant} disabled={uploading} onClick={uploadDirectly} style={{ textTransform: 'none' }}>
        {uploading ? 'Uploading...' : 'Upload Video'}
      </Button>
      {error && (
        <Typography
          variant="body1"
          sx={{
            position: 'absolute',
            bottom: '-24px',
            fontSize: '12px',
            color: 'var(--down)',
          }}
        >
          {error}
        </Typography>
      )}
      <input ref={uploadRef} type="file" accept=".mp4" onChange={onUploadVideo} style={{ display: 'none' }} />
    </>
  );
};

interface ISingleVideo extends ICommonProps {
  title: string;
  link: string;
  noEdit?: boolean;
  noRecord?: boolean;
  onChange?: Function;
}
export const SingleVideo: React.FC<ISingleVideo> = ({ keyName, user, title, link, noEdit, noRecord, onChange }) => {
  const navigate = useNavigate();
  // @ts-ignore
  const [videoUrl, setVideoUrl] = React.useState(user?.[keyName] || '');
  const [showVideo, setShowVideo] = React.useState(true);

  const videoBox = React.useMemo(() => {
    if (showVideo) {
      if (videoUrl)
        return (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video height="100%" width="300px" style={{ borderRadius: '10px' }} controls controlsList="nodownload">
            <source src={videoUrl} type="video/webm" />
          </video>
        );
      return (
        <img
          width="50px"
          height="50px"
          src="https://cdn-icons-png.flaticon.com/512/120/120614.png"
          alt="no video"
          style={{
            border: '0px solid',
            padding: '10px',
            borderRadius: '50%',
            overflow: 'visible',
          }}
        />
      );
    }
    setTimeout(() => setShowVideo(true), 500);
    return <div />;
  }, [videoUrl, showVideo]);

  const goToPage = () => {
    navigate(`/pro/recording/${link}`);
  };

  const onVideoComplete = (url: string) => {
    setVideoUrl(url);
    setShowVideo(false);
    if( onChange ) { onChange( url ) }
  };

  if (!user || (noEdit && !videoUrl)) return <div />;
  return (
    <Box
      minWidth={`${noEdit ? 25 : 30}%`}
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        padding: '10px',
        border: '0px solid var(--border)',
        borderRadius: '2px',
        textAlign: 'center',
      }}
    >
      <Typography variant="h5">{title}</Typography>
      <Box margin="10px 0" display="flex" alignItems="center" justifyContent="center" minHeight="300px" border={!videoUrl ? '1px solid var(--text)' : ''} borderRadius="10px" minWidth="300px">
        {videoBox}
      </Box>
      {!noEdit && (
        <Box display="flex" alignItems="center" position="relative" gap={1.5} justifyContent="space-between">
          { !noRecord &&
            <Button
              variant="contained"
              onClick={goToPage}
              sx={{
                background: '#4437d0',
                textTransform: 'none',
                ':hover': {
                  background: '#1a0dab',
                },
              }}
            >
              Record Video
            </Button>
          }
          <DirectUpload keyName={keyName} onComplete={onVideoComplete} />
        </Box>
      )}
    </Box>
  );
};

interface Props {
  data?: ISinglePro;
}
export const MyIntroVideos: React.FC<Props> = ({ data }) => {
  const [user, setUser] = React.useState(data);

  React.useEffect(() => {
    if (!data) {
      axiosInstant
        .get(myInfo(), {})
        .then((response) => {
          setUser(response.data);
        })
        .catch((error) => {});
    }
  }, [data]);

  if (!user) return <DotLottleLoader fill={false} />;

  return (
    <>
      {!data && (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" className="text-3xl font-bold uppercase font-[CircularStd]" color="primary">
            Intro Videos
          </Typography>
          <Link className="cursor-pointer" to="/settings/profile">
            Back
          </Link>
        </Box>
      )}
      {!!(user.video1 || user.video2) && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginTop={data ? '50px' : '30px'}
          gap={data ? '50px' : '30px'}
          className="mt-3"
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <SingleVideo keyName="video1" user={user} title="Introduction" link="intro" noEdit={!!data} />
          <SingleVideo keyName="video2" user={user} title="Additional Info" link="additional-info" noEdit={!!data} />
        </Box>
      )}
    </>
  );
};

export default MyIntroVideos;
